import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { BRAND_NAME } from "@/config";

const HERO = "https://static.prod-images.emergentagent.com/jobs/d1db4199-e454-4641-b980-62c2bcce64aa/images/e1d5f539fe02471888047c703fef228e4599d909b9bc0922adcc7be59393ccc2.png";

export default function Home() {
  const { categories } = useCategories();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data));
  }, []);

  const items = products;
  const imageForCategory = (cat) => {
    if (cat.image) return resolveImg(cat.image);
    const p = products.find((x) => x.category === cat.slug && x.images?.length);
    return p ? resolveImg(p.images[0]) : HERO;
  };

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="home-page">
      <Header />

      {/* Hero */}
      <section className="relative h-[40vh] min-h-[280px] w-full overflow-hidden" data-testid="hero">
        <img src={HERO} alt="Fine jewellery" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white">
          <p className="mb-3 text-xs uppercase tracking-[0.35em]">The New Collection</p>
          <h1 className="font-serif text-3xl font-light leading-none tracking-tight sm:text-4xl lg:text-5xl">
            Timeless brilliance.
          </h1>
          <Link
            to={categories[0] ? `/category/${categories[0].slug}` : "#"}
            data-testid="hero-cta"
            className="mt-6 border border-white px-7 py-3 text-xs uppercase tracking-[0.18em] transition-colors hover:bg-white hover:text-stone-900"
          >
            Discover
          </Link>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-[1400px] px-5 py-12 md:px-10 md:py-16" data-testid="categories-section">
        <div className="mb-10 text-center">
          <p className="text-xs uppercase tracking-[0.28em] text-stone-400">Explore</p>
          <h2 className="mt-2 font-serif text-2xl tracking-tight text-stone-900 md:text-3xl">The Collections</h2>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          {categories.map((c, i) => (
            <Link
              key={c.id}
              to={`/category/${c.slug}`}
              data-testid={`home-cat-${c.slug}`}
              className="group relative aspect-[4/3] overflow-hidden bg-secondary animate-fade-up"
              style={{ animationDelay: `${(i % 3) * 70}ms` }}
            >
              <img src={imageForCategory(c)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
              <div className="absolute inset-x-0 bottom-0 flex flex-col items-center pb-5 text-white">
                <span className="font-serif text-lg tracking-wide md:text-xl">{c.name}</span>
                <span className="mt-1 text-[11px] uppercase tracking-[0.2em] opacity-0 transition-opacity group-hover:opacity-100">Shop now</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Our Collection */}
      {items.length > 0 && (
        <section className="border-t border-border bg-white" data-testid="featured-section">
          <div className="mx-auto max-w-[1400px] px-5 py-12 md:px-10 md:py-16">
            <div className="mb-10 text-center">
              <p className="text-xs uppercase tracking-[0.28em] text-stone-400">{BRAND_NAME}</p>
              <h2 className="mt-2 font-serif text-2xl tracking-tight text-stone-900 md:text-3xl">Featured Products</h2>
            </div>
            <div className="grid grid-cols-2 gap-5 md:grid-cols-5 md:gap-6">
              {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} compact />)}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
